import React, { Suspense, lazy } from 'react';
import { useTranslate } from 'hooks/useTranslate';
import { getLanguage } from 'translation';
// Lazy — Onboarding pulls @mui/material/Dialog (and the rest of the
// @mui/material chunk transitively), and it's below-the-fold on home.
// Eager import here was forcing ~144 KB-gzipped of MUI onto the LCP race.
const Onboarding = lazy(() => import('pages/Onboarding'));
const About = lazy(() => import('./components/About'));
// TestStart is the LCP target on the home route (the "Want to find clarity
// about your future?" hero). Lazy-loading it forced an extra chunk fetch +
// parse before LCP could fire, costing ~1.5s on simulated mobile. Eager
// import keeps it in the entry chunk; the rest of the home sections stay
// lazy because they're below the fold.
import TestStart from './components/TestStart';
const ProTeaser = lazy(() => import('./components/ProTeaser'));
const Services = lazy(() => import('./components/Services'));
const GetConsultation = lazy(() => import('./components/GetConsultation'));
const Media = lazy(() => import('./components/Media'));
const HomeProfessions = lazy(() => import('./components/HomeProfessions'));
const Footer = lazy(() => import('components/Footer'));
const LinearProgress = lazy(() => import('components/LinearProgress'));
const MainLayout = lazy(() => import('layouts/MainLayout'));
const Header = lazy(() => import('components/Header'));
// Helmet/SEO must be eagerly imported under React 19. As a lazy chunk it
// gets mounted *after* its Suspense fallback first renders, and the SEO
// component's <title>/<meta>/<link> get hoisted on each suspend→resume
// cycle without React 19 deduping the previous ones — we observed up to
// 5 duplicate <title> tags in <head> with lazy(). Static keeps it to one.
import Helmet from 'components/Helmet';
const LazyComponent = lazy(() => import('components/LazyComponent'));

const Home = () => {
  const tHome = useTranslate('Home');
  const [lang] = React.useState(() => getLanguage());
  // Seed every section-visibility flag to true so the home page renders its
  // full height on the first commit. The previous IS_SNAP-only seed made the
  // page render ~714 px tall on real clients (sections gated behind a chain
  // of intersection observers), which put the Footer inside the viewport
  // and turned the React-mount wipe into a 0.24 CLS spike on Lighthouse.
  // The IO chain stays in place for any future deferred work, but initial
  // structure is no longer hidden behind it.
  const [isAboutVisible, setIsAboutVisible] = React.useState(true);
  const [isServicesVisible, setIsServicesVisible] = React.useState(true);
  const [isProfessionsVisible, setIsProfessionsVisible] = React.useState(true);
  const [isConsultationVisible, setIsConsultationVisible] = React.useState(true);

  const texts = React.useMemo(() => {
    const f = (k) => ({ [lang]: tHome(k) });
    return {
      hero_headline: f('HeroHeadline'),
      hero_subheadline: f('HeroSubheadline'),
      hero_button: f('HeroButton1'),
      about_field1: f('AboutField1'),
      about_field2: f('AboutField2'),
      about_field3: f('AboutField3'),
      value_headline: f('ValueHeadline'),
      value_subheadline: f('ValueSubheadline'),
      value_1_header: f('Value1Header'),
      value_1_text: f('Value1Text'),
      value_2_header: f('Value2Header'),
      value_2_text: f('Value2Text'),
      value_3_header: f('Value3Header'),
      value_3_text: f('Value3Text'),
      value_4_header: f('Value4Header'),
      value_4_text: f('Value4Text'),
      value_5_header: f('Value5Header'),
      value_5_text: f('Value5Text'),
      value_6_header: f('Value6Header'),
      value_6_text: f('Value6Text'),
      press_headline: f('PressHeadline'),
      press_subheadline: f('PressSubheadline'),
      occupations_headline: f('OccupationsHeadline'),
      occupations_subheadline: f('OccupationsSubheadline'),
      occupations_button: f('OccupationsButton')
    };
  }, [tHome, lang]);

  return (
    <>
      <Helmet
        page={'home'}
        includeHreflang={true}
      />
      <MainLayout>
        <Header />
        {/* TestStart is eagerly imported (see above) so it renders on the
            first commit instead of waiting on a lazy chunk + Suspense
            fallback cycle. Bypass LazyComponent's IntersectionObserver
            here — the hero IS the initial viewport. `isAboutVisible` is
            already seeded to true at the top, so the below-the-fold
            sections still render. */}
        <TestStart />
        {isAboutVisible && (
          <Suspense fallback={null}>
            <LazyComponent component={ProTeaser} />
            <LazyComponent component={About} texts={texts} />
            <LazyComponent component={Services} texts={texts} setIsVisible={setIsServicesVisible} />
          </Suspense>
        )}
        {isServicesVisible && (
          <Suspense fallback={null}>
            <LazyComponent component={HomeProfessions} texts={texts} setIsVisible={setIsProfessionsVisible} />
          </Suspense>
        )}
        {isProfessionsVisible && (
          <Suspense fallback={null}>
            <LazyComponent component={GetConsultation} texts={texts} setIsVisible={setIsConsultationVisible} />
          </Suspense>
        )}
        {isConsultationVisible && (
          <Suspense fallback={null}>
            <LazyComponent component={Media} texts={texts} />
            <Onboarding />
          </Suspense>
        )}
        <Suspense fallback={null}>
          <LazyComponent component={Footer} />
        </Suspense>
      </MainLayout>
    </>
  );
};

export default Home;
